// src/pages/FacultyProfile.jsx
import { useState, useEffect, useRef } from "react";

import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { SECTIONS, SECTION_FIELDS } from "../constants/sections";
import { useDarkMode, DM } from "../context/DarkModeContext";
import { INDIAN_STATES_DISTRICTS } from "../constants/indiaStates";
import DashboardHeader from "../components/DashboardHeader";
import { api } from "../utils/api";

function getProfileDepartment(profile) {
  const source = profile?.data || profile || {};
  return (source.department || source.departmentName || profile?.department || profile?.departmentName || "").toString().trim();
}

// Compress image to base64 (max 120x120, 50% quality) for Firestore storage
function compressImageToBase64(file) {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error("Image processing timed out. Please try a smaller image.")), 8000);
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        clearTimeout(timeout);
        const maxSize = 120; 
        let w = img.width, h = img.height;
        if (w > h) { if (w > maxSize) { h = Math.round(h * maxSize / w); w = maxSize; } }
        else { if (h > maxSize) { w = Math.round(w * maxSize / h); h = maxSize; } }
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error("Browser does not support canvas scaling")); return; }
        ctx.drawImage(img, 0, 0, w, h);
        try {
          // Use low quality to keep string length very short
          resolve(canvas.toDataURL('image/jpeg', 0.5));
        } catch (err) {
          reject(new Error("Failed to convert image to data URL."));
        }
      };
      img.onerror = () => { clearTimeout(timeout); reject(new Error("Failed to load image file.")); };
      img.src = e.target.result;
    };
    reader.onerror = () => { clearTimeout(timeout); reject(new Error("Failed to read image file.")); };
    reader.readAsDataURL(file);
  });
}


export default function FacultyProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { logout, userRole } = useAuth();
  const isAdmin = userRole === "admin" || userRole === "superadmin";
  const [searchParams] = useSearchParams();
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditEntryModal, setShowEditEntryModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeSection, setActiveSection] = useState(searchParams.get("tab") || "general_info");
  const [toast, setToast] = useState("");
  const [departments, setDepartments] = useState([]);
  const [colleges, setColleges] = useState(["Adhiparasakthi Dental College and Hospital"]);
  const [designations, setDesignations] = useState([]);
  const [profileFields, setProfileFields] = useState([]);
  const { darkMode, toggleDarkMode } = useDarkMode();
  const d = darkMode;

  useEffect(() => { fetchProfile(); loadOptions(); }, [id]);

  async function fetchProfile() {
    try {
      const res = await api.get(`/faculty/${id}/profile`);
      let profileData = { id, data: res, ...res };
      
      // Fetch all section data from separate API endpoints
      for (const section of SECTIONS) {
        try {
          const sectionRes = await api.get(`/faculty/${id}/section/${section.key}`);
          // Handle both array and single object responses
          let sectionData = Array.isArray(sectionRes) ? sectionRes : [];
          // Map each row to include id with the data
          profileData[section.key] = sectionData.map(row => ({
            id: row.id,
            ...(typeof row.data === 'string' ? JSON.parse(row.data) : row.data || row)
          }));
        } catch (err) {
          console.warn(`Failed to fetch ${section.key}:`, err);
          profileData[section.key] = [];
        }
      }
      
      setProfile(profileData);
      
      const ffRes = await api.get(`/faculty_profile_fields?orderBy=order`);
      const fields = Array.isArray(ffRes) ? ffRes : [];
      setProfileFields(fields);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  const getNameWithTitle = (p) => {
    if (!p) return "";
    const title = p.title || p.salutation || p.prefix || "";
    if (!title) return p.name || "";
    return `${title} ${p.name}`.trim();
  };

  async function loadOptions() {
    try {
      const dRes = await api.get(`/departments?orderBy=name`);
      const depts = Array.isArray(dRes) ? dRes : [];
      setDepartments(depts.map(d => d.name || d));
      
      const cRes = await api.get(`/colleges?orderBy=name`);
      const coll = Array.isArray(cRes) ? cRes : [];
      setColleges(["Adhiparasakthi Dental College and Hospital", ...coll.map(c => c.name || c)]);
      
      const desRes = await api.get(`/designations?orderBy=name`);
      const desigs = Array.isArray(desRes) ? desRes : [];
      setDesignations(desigs.map(d => d.name || d));
    } catch {}
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 3500); }
  async function handleLogout() { await logout(); navigate(isAdmin ? "/admin" : "/login"); }

  async function deleteItem(sectionKey, item) {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/faculty/${id}/section/${sectionKey}/${item.id}`);
      fetchProfile();
      showToast("✅ Entry deleted");
    } catch (err) { console.error("Delete error:", err); showToast("❌ Failed to delete"); }
  }

  function openEditEntry(item) {
    setEditingItem(item);
    setShowEditEntryModal(true);
  }

  async function saveEditEntry(sectionKey, oldItem, newItem) {
    try {
      await api.put(`/faculty/${id}/section/${sectionKey}/${oldItem.id}`, {
        ...newItem,
        updatedAt: new Date().toISOString()
      });
      fetchProfile();
      showToast("✅ Entry updated successfully!");
    } catch { showToast("❌ Failed to update"); }
  }

  const activeSectionData = SECTIONS.find(s => s.key === activeSection);
  const departmentValue = getProfileDepartment(profile);

  if (loading) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: DM.pageBg(d), color: "#94a3b8", fontSize: 20 }}>⏳ Loading Profile...</div>;
  if (!profile || !profile.id) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: DM.pageBg(d), flexDirection: "column", gap: 20 }}>
    <div style={{ fontSize: 42 }}>🔎</div>
    <div style={{ fontWeight: 700, color: "#475569" }}>Faculty Profile not found.</div>
    <button onClick={() => navigate(isAdmin ? "/admin/dashboard" : "/dashboard")} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontWeight: 700, cursor: "pointer" }}>Back to Dashboard</button>
  </div>;

  return (
    <div style={{ minHeight: "100vh", background: DM.pageBg(d), fontFamily: "Georgia, serif", transition: "background 0.3s" }}>

      <DashboardHeader 
        label={isAdmin ? "ADMIN" : "FACULTY"}
        showNewsTicker={true}
      >
        <button onClick={() => navigate(isAdmin ? "/admin/dashboard" : "/dashboard")} style={{ background: "#fff", color: "#1d4ed8", border: "none", borderRadius: 10, padding: "8px 18px", fontWeight: 800, fontSize: 16, cursor: "pointer" }}>← Dashboard</button>
        <button onClick={toggleDarkMode} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 14px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>
          {darkMode ? "☀️ Light" : "🌙 Dark"}
        </button>
        <button onClick={handleLogout} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "8px 16px", fontWeight: 600, fontSize: 15, cursor: "pointer" }}>🚪 Logout</button>
      </DashboardHeader>

      {toast && (
        <div style={{ position: "fixed", top: 80, right: 24, zIndex: 999, background: toast.startsWith("✅") ? "#f0fdf4" : "#fef2f2", border: `1px solid ${toast.startsWith("✅") ? "#86efac" : "#fecaca"}`, color: toast.startsWith("✅") ? "#16a34a" : "#dc2626", borderRadius: 12, padding: "14px 20px", fontWeight: 700, fontSize: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
          {toast}
        </div>
      )}

      {!departmentValue && (
        <div style={{ margin: "20px 28px 0", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 12, padding: "14px 18px", color: "#92400e", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <span>⚠️ Your profile is missing a department. Please update it with the correct department.</span>
          <button onClick={() => setShowEditModal(true)} style={{ background: "#92400e", color: "#fff", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, cursor: "pointer" }}>Update Profile</button>
        </div>
      )}

      <div style={{ display: "flex", flex: 1 }}>

        {/* Sidebar */}
        <aside style={{ width: 230, background: DM.sidebarBg(d), boxShadow: DM.shadow(d), paddingTop: 16, display: "flex", flexDirection: "column", overflowY: "auto", minHeight: "calc(100vh - 64px)", transition: "background 0.3s" }}>
          {/* Profile mini card */}
          <div style={{ padding: "16px 16px 12px", borderBottom: "1px solid #f1f5f9", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {profile.profilePhotoURL
                ? <img src={profile.profilePhotoURL} alt={profile.name} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", border: "2px solid #e2e8f0", flexShrink: 0 }} />
                : <div style={{ width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg,#f7a84f,#f74f6e)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 900, color: "#fff" }}>
                    {profile.name?.[0]?.toUpperCase()}
                  </div>
              }
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b" }}>{profile.name}</div>
                <div style={{ fontSize: 13, color: "#94a3b8" }}>{profile.empId}</div>
              </div>
            </div>
            <button onClick={() => setShowEditModal(true)}
              style={{ width: "100%", marginTop: 10, background: "#eaf1ff", color: "#2563eb", border: "none", borderRadius: 8, padding: "7px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
              ✏️ Edit Profile
            </button>
          </div>

          <div style={{ padding: "4px 16px 8px", fontSize: 13, fontWeight: 700, color: "#94a3b8", letterSpacing: 1 }}>PROFILE</div>
          <div onClick={() => setActiveSection("general_info")}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", cursor: "pointer", borderLeft: activeSection === "general_info" ? `3px solid #6366f1` : "3px solid transparent", background: activeSection === "general_info" ? "#f5f3ff" : "transparent", color: activeSection === "general_info" ? "#6366f1" : "#475569", fontWeight: activeSection === "general_info" ? 700 : 400, fontSize: 15, transition: "all 0.15s" }}>
            <span style={{ fontSize: 17 }}>👤</span>
            <span style={{ flex: 1 }}>General Info</span>
          </div>

          <div style={{ padding: "12px 16px 8px", fontSize: 13, fontWeight: 700, color: "#94a3b8", letterSpacing: 1 }}>SECTIONS</div>
          {SECTIONS.map(sec => {
            const count = profile[sec.key]?.length || 0;
            return (
              <div key={sec.key} onClick={() => setActiveSection(sec.key)}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", cursor: "pointer", borderLeft: activeSection === sec.key ? `3px solid ${sec.color}` : "3px solid transparent", background: activeSection === sec.key ? sec.light : "transparent", color: activeSection === sec.key ? sec.color : "#475569", fontWeight: activeSection === sec.key ? 700 : 400, fontSize: 15, transition: "all 0.15s" }}>
                <span style={{ fontSize: 17 }}>{sec.icon}</span>
                <span style={{ flex: 1, lineHeight: 1.3 }}>{sec.label}</span>
                {count > 0 && <span style={{ background: sec.color, color: "#fff", borderRadius: 20, padding: "1px 7px", fontSize: 13, fontWeight: 700 }}>{count}</span>}
              </div>
            );
          })}
        </aside>

        {/* Main content */}
        <main style={{ flex: 1, padding: "24px 28px", overflowY: "auto" }}>

          {/* Profile Banner */}
          <div style={{ background: "linear-gradient(120deg,#0f2557,#1a3a8f,#2563eb)", borderRadius: 20, padding: "24px 32px", color: "#fff", display: "flex", alignItems: "center", gap: 24, marginBottom: 20, boxShadow: "0 8px 32px rgba(37,99,235,0.18)", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", right: -40, top: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
            <div style={{ width: 72, height: 72, borderRadius: "50%", border: "3px solid rgba(255,255,255,0.35)", flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#f7a84f,#f74f6e)" }}>
              {profile.profilePhotoURL
                ? <img src={profile.profilePhotoURL} alt={profile.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : <span style={{ fontSize: 30, fontWeight: 900, color: "#fff" }}>{profile.name?.[0]?.toUpperCase()}</span>
              }
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 24, fontWeight: 900 }}>{getNameWithTitle(profile)}</div>
              <div style={{ display: "flex", gap: "4px 14px", flexWrap: "wrap", marginTop: 6 }}>
                {profile.designation && <span style={{ fontSize: 14, background: "rgba(255,255,255,0.12)", borderRadius: 6, padding: "2px 10px", opacity: 0.9 }}>🎓 {profile.designation}</span>}
                {departmentValue && <span style={{ fontSize: 14, background: "rgba(255,255,255,0.12)", borderRadius: 6, padding: "2px 10px", opacity: 0.9 }}>🏢 {departmentValue}</span>}
              </div>
              <div style={{ opacity: 0.65, fontSize: 14, marginTop: 6, display: "flex", gap: 14, flexWrap: "wrap" }}>
                {profile.institution && <span>🏛️ {profile.institution}</span>}
                {profile.empId && <span>🪪 {profile.empId}</span>}
                {profile.mobile && <span>📱 {profile.mobile}</span>}
                {profile.email && <span>✉️ {profile.email}</span>}
              </div>
            </div>
          </div>

          {/* Stats Bar */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 10, marginBottom: 20 }}>
            <div onClick={() => setActiveSection("general_info")}
              style={{ background: "#fff", borderRadius: 12, padding: "12px 8px", boxShadow: "0 2px 8px rgba(15,37,87,0.07)", borderTop: `3px solid #6366f1`, cursor: "pointer", textAlign: "center", transform: activeSection === "general_info" ? "translateY(-2px)" : "none", transition: "transform 0.15s" }}>
              <div style={{ fontSize: 18, marginBottom: 3 }}>👤</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#6366f1" }}>100%</div>
              <div style={{ fontSize: 13, color: "#666", fontWeight: 600, marginTop: 2 }}>Profile</div>
            </div>
            {SECTIONS.map((s, i) => (
              <div key={i} onClick={() => setActiveSection(s.key)}
                style={{ background: "#fff", borderRadius: 12, padding: "12px 8px", boxShadow: "0 2px 8px rgba(15,37,87,0.07)", borderTop: `3px solid ${s.color}`, cursor: "pointer", textAlign: "center", transform: activeSection === s.key ? "translateY(-2px)" : "none", transition: "transform 0.15s" }}>
                <div style={{ fontSize: 18, marginBottom: 3 }}>{s.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{profile[s.key]?.length || 0}</div>
                <div style={{ fontSize: 13, color: "#666", fontWeight: 600, marginTop: 2, lineHeight: 1.3 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Active Section Content */}
          <div style={{ background: DM.cardBg(d), borderRadius: 18, boxShadow: DM.shadow(d), overflow: "hidden" }}>
            {activeSection === "general_info" ? (
              <div>
                <div style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#f8fafc" }}>
                  <h2 style={{ fontWeight: 800, fontSize: 17, color: "#0f2557", margin: 0 }}>👤 General Information</h2>
                  <button onClick={() => setShowEditModal(true)} style={{ background: "#eaf1ff", color: "#2563eb", border: "none", borderRadius: 10, padding: "7px 16px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>✏️ Edit Basic Info</button>
                </div>
                <div style={{ padding: "24px" }}>
                  {(() => {
                    const allFields = [
                      { label: "Full Name", val: getNameWithTitle(profile), icon: "👤", group: "Staff Details" },
                      { label: "Staff ID / Emp ID", val: profile.empId, icon: "🪪", group: "Staff Details" },
                      { label: "Email Address", val: profile.email, icon: "✉️", group: "Contact Details" },
                      { label: "Mobile Number", val: profile.mobile, icon: "📱", group: "Contact Details" },
                      { label: "Department", val: profile.department, icon: "🏢", group: "Job Details" },
                      { label: "Designation", val: profile.designation, icon: "🎓", group: "Job Details" },
                      { label: "Institution", val: profile.institution, icon: "🏛️", group: "Job Details" },
                      ...profileFields.map(f => ({
                        label: f.name,
                        val: profile[f.key || f.name.toLowerCase().replace(/[^a-z0-9]/g, "_")],
                        icon: "🔹",
                        group: f.group || "Other Details"
                      }))
                    ];

                    const groups = allFields.reduce((acc, f) => {
                      const g = f.group || "Other Details";
                      if (!acc[g]) acc[g] = [];
                      acc[g].push(f);
                      return acc;
                    }, {});

                    const groupOrder = ["STAFF DETAILS", "PERSONAL DETAILS", "CONTACT DETAILS", "JOB DETAILS"];
                    const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
                      const idxA = groupOrder.indexOf(a.toUpperCase());
                      const idxB = groupOrder.indexOf(b.toUpperCase());
                      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                      if (idxA !== -1) return -1;
                      if (idxB !== -1) return 1;
                      return a.localeCompare(b);
                    });

                    return (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "24px" }}>
                        {sortedGroups.map(([groupName, fields]) => {
                          const filledFields = fields.filter(f => f.val && f.val.toString().trim() !== "");
                          if (filledFields.length === 0) return null;

                          return (
                            <div key={groupName} style={{ background: "#fbfcfe", borderRadius: 20, border: "1.5px solid #eef2f6", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 4px 12px rgba(0,0,0,0.02)" }}>
                              <div style={{ padding: "14px 20px", background: "#f8fafc", borderBottom: "1px solid #eef2f6", display: "flex", alignItems: "center", gap: 10 }}>
                                 <div style={{ width: 4, height: 18, background: "#2563eb", borderRadius: 2 }}></div>
                                 <span style={{ fontSize: 13, fontWeight: 900, color: "#475569", letterSpacing: 1 }}>{groupName.toUpperCase()}</span>
                              </div>
                              <div style={{ padding: "20px", display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px 24px" }}>
                                {filledFields.map((item, idx) => (
                                  <div key={idx} style={{ minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 800, color: "#94a3b8", letterSpacing: 0.5, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
                                      <span>{item.icon}</span> {item.label.toUpperCase()}
                                    </div>
                                    <div style={{ fontSize: 17, fontWeight: 700, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={item.val}>
                                      {item.val}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  
                  {/* Designation History */}
                  {profile.designationHistory?.length > 0 && (
                    <div style={{ marginTop: 12, borderTop: "1px solid #f1f5f9", paddingTop: 20 }}>
                       <div style={{ fontSize: 13, fontWeight: 800, color: "#0f2557", display: "flex", alignItems: "center", gap: 8, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>
                        <span style={{ fontSize: 18 }}>📜</span> Institutional Designation History
                      </div>
                      <div style={{ overflowX: "auto", background: "#f8fafc", borderRadius: 12, border: "1px solid #e2e8f0" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                          <thead>
                            <tr style={{ textAlign: "left" }}>
                              <th style={{ padding: "10px 12px", color: "#64748b", fontWeight: 700, borderBottom: "1.5px solid #e2e8f0" }}>S.No</th>
                              <th style={{ padding: "10px 12px", color: "#64748b", fontWeight: 700, borderBottom: "1.5px solid #e2e8f0" }}>Department</th>
                              <th style={{ padding: "10px 12px", color: "#64748b", fontWeight: 700, borderBottom: "1.5px solid #e2e8f0" }}>Designation</th>
                              <th style={{ padding: "10px 12px", color: "#64748b", fontWeight: 700, borderBottom: "1.5px solid #e2e8f0" }}>Unit</th>
                              <th style={{ padding: "10px 12px", color: "#64748b", fontWeight: 700, borderBottom: "1.5px solid #e2e8f0" }}>Period</th>
                            </tr>
                          </thead>
                          <tbody>
                            {profile.designationHistory.map((des, idx) => (
                              <tr key={idx} style={{ borderTop: "1px solid #f1f5f9", background: des.isMain ? "#fff" : "transparent" }}>
                                <td style={{ padding: "8px 12px", color: "#94a3b8" }}>{idx + 1}</td>
                                <td style={{ padding: "8px 12px", fontWeight: 600 }}>{des.dept}</td>
                                <td style={{ padding: "8px 12px" }}>
                                  <div style={{ fontWeight: 700, color: "#1e293b" }}>{des.designation}</div>
                                  {des.isMain && <span style={{ fontSize: 13, background: "#dcfce7", color: "#16a34a", borderRadius: 4, padding: "1px 5px", fontWeight: 800 }}>PRIMARY ROLE</span>}
                                </td>
                                <td style={{ padding: "8px 12px", fontWeight: 600 }}>{des.unit || "—"}</td>
                                <td style={{ padding: "8px 12px", fontSize: 13, color: "#64748b" }}>
                                  <span style={{ fontWeight: 700, color: "#334155" }}>{(des.fromDate || "").split("-").reverse().join("-") || "—"}</span> 
                                  <span style={{ margin: "0 4px", opacity: 0.5 }}>→</span>
                                  <span style={{ fontWeight: 700, color: "#334155" }}>{(des.toDate || "").split("-").reverse().join("-") || "Till Date"}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div style={{ padding: "16px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "center", background: activeSectionData?.light }}>
                  <h2 style={{ fontWeight: 800, fontSize: 17, color: "#0f2557", margin: 0 }}>
                    {activeSectionData?.icon} {activeSectionData?.label} ({profile[activeSection]?.length || 0})
                  </h2>
                  <button onClick={() => setShowAddModal(true)}
                    style={{ background: activeSectionData?.color, color: "#fff", border: "none", borderRadius: 10, padding: "7px 16px", fontWeight: 700, cursor: "pointer", fontSize: 15 }}>
                    + Add Entry
                  </button>
                </div>

                <div style={{ padding: "16px 24px" }}>
                  {!profile[activeSection]?.length ? (
                    <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                      <div style={{ fontSize: 38, marginBottom: 10 }}>{activeSectionData?.icon}</div>
                      <p>No {activeSectionData?.label} entries yet</p>
                      <button onClick={() => setShowAddModal(true)} style={{ marginTop: 12, background: activeSectionData?.light, color: activeSectionData?.color, border: "none", borderRadius: 10, padding: "8px 20px", fontWeight: 700, cursor: "pointer" }}>+ Add First Entry</button>
                    </div>
                  ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                      {profile[activeSection].map((item, idx) => (
                        <DynamicItemCard key={idx} item={item} sectionKey={activeSection}
                          onEdit={() => openEditEntry(item)}
                          onDelete={() => deleteItem(activeSection, item)} />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {showEditModal && (
        <EditProfileModal
          profile={profile}
          userId={id}
          departments={departments}
          colleges={colleges}
          designations={designations}
          profileFields={profileFields}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => { setShowEditModal(false); fetchProfile(); setToast("✅ Profile updated!"); }}
        />
      )}

      {showAddModal && (
        <AddItemModal section={activeSection} sectionData={activeSectionData} userId={id}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { setShowAddModal(false); fetchProfile(); showToast("✅ Entry added!"); }} />
      )}

      {showEditEntryModal && editingItem && (
        <EditEntryModal
          section={activeSection}
          sectionData={activeSectionData}
          item={editingItem}
          userId={id}
          onClose={() => { setShowEditEntryModal(false); setEditingItem(null); }}
          onSuccess={(oldItem, newItem) => { saveEditEntry(activeSection, oldItem, newItem); setShowEditEntryModal(false); setEditingItem(null); }}
        />
      )}
    </div>
  );
}

function AddItemModal({ section, sectionData, userId, onClose, onSuccess }) {
  const [form, setForm] = useState({});
  const [pendingFiles, setPendingFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [error, setError] = useState("");
  const [fields, setFields] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    async function loadFields() {
      try {
        // Fetch from backend API with order sorting
        const fieldsData = await api.get(`/section_fields_${section}?orderBy=order`);
        if (Array.isArray(fieldsData)) {
          // Sort by order to ensure correct display
          const sorted = fieldsData.sort((a, b) => (a.order || 0) - (b.order || 0));
          setFields(sorted);
          console.log(`[AddItemModal] Loaded ${sorted.length} fields for ${section}:`, sorted);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (e) {
        console.warn(`[AddItemModal] Failed to load fields from API for ${section}:`, e);
        // Fallback to hardcoded fields if API fails
        setFields(SECTION_FIELDS[section] || []);
      }
      setFieldsLoading(false);
    }
    loadFields();
  }, [section]);

  function getKey(f) { return f.key || f.name?.toLowerCase().replace(/[^a-z0-9]/g, "_"); }

  async function handleAdd() {
    const req = fields.find(f => f.required && !form[getKey(f)]?.toString().trim());
    if (req) return setError(`${req.label || req.name} is required`);
    try {
      setLoading(true);
      let finalForm = { ...form };
      await api.post(`/faculty/${userId}/section/${section}`, {
        ...finalForm,
        addedAt: new Date().toISOString()
      });
      onSuccess();
    } catch (err) { console.error(err); setError("Failed to add. Try again."); }
    finally { setLoading(false); }
  }

  const inputStyle = { width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "9px 12px", fontSize: 15, color: "#1e293b", outline: "none", boxSizing: "border-box", fontFamily: "Georgia, serif" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 24, padding: "32px", width: "100%", maxWidth: 620, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 32px 64px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 22 }}>
          <h2 style={{ fontWeight: 800, color: "#0f2557", fontSize: 22 }}>{sectionData?.icon} Add {sectionData?.label}</h2>
          <span onClick={onClose} style={{ fontSize: 24, cursor: "pointer", color: "#94a3b8" }}>✕</span>
        </div>
        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px", color: "#dc2626", fontSize: 15, marginBottom: 14 }}>{error}</div>}
        {fieldsLoading ? <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8" }}>⏳ Loading...</div> : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {fields.map((f, i) => {
              const k = getKey(f);
              const type = (f.type || "text").toLowerCase();
              return (
                <div key={i} style={{ gridColumn: type === "textarea" ? "1/-1" : "auto" }}>
                  <label style={{ fontSize: 14, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                    {f.label || f.name} {f.required && <span style={{ color: "#dc2626" }}>*</span>}
                  </label>
                  {type === "dropdown" ? (
                    <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                      <option value="">-- Select --</option>
                      {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : type === "textarea" ? (
                    <textarea value={form[k] || ""} onChange={e => set(k, e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
                  ) : type === "file" ? (
                    <div>
                      <input type="file" onChange={e => setPendingFiles(prev => ({ ...prev, [k]: e.target.files[0] }))} style={{ ...inputStyle, padding: "6px 12px" }} />
                      {pendingFiles[k] && <div style={{ fontSize: 13, color: "#16a34a", marginTop: 4 }}>📎 Selected: {pendingFiles[k].name}</div>}
                    </div>
                  ) : type === "number" ? (
                    <input type="number" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  ) : type === "date" ? (
                    <input type="date" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  ) : (type === "month-year" || type === "Month-Year") ? (
                    <input type="month" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  ) : (
                    <input type="text" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: "flex", gap: 12, marginTop: 22 }}>
          <button onClick={onClose} style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 12, padding: "12px", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleAdd} disabled={loading} style={{ flex: 2, background: loading ? "#93c5fd" : `linear-gradient(135deg,${sectionData?.color},${sectionData?.color}cc)`, color: "#fff", border: "none", borderRadius: 12, padding: "12px", fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", fontFamily: "Georgia, serif" }}>
            {loading ? "⏳ Saving..." : "✅ Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditProfileModal({ profile, userId, departments, colleges, designations = [], profileFields = [], onClose, onSuccess }) {
  const [form, setForm] = useState({ 
    institution: "Adhiparasakthi Dental College and Hospital",
    designationHistory: (profile.designationHistory || [
      { dept: profile.department || "", designation: profile.designation || "", unit: "", fromDate: "", toDate: "", isMain: true }
    ]),
    ...profile 
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(profile.profilePhotoURL || null);
  const photoInputRef = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleDesigHistoryChange = (index, field, value) => {
    const newData = [...form.designationHistory];
    if (field === "isMain" && value === true) {
      newData.forEach((item, i) => item.isMain = (i === index));
      // Sync with core profile
      set("department", newData[index].dept);
      set("designation", newData[index].designation);
    } else {
      newData[index][field] = value;
    }
    set("designationHistory", newData);
  };
  const addDesigHistoryRow = () => set("designationHistory", [...form.designationHistory, { dept: "", designation: "", unit: "", fromDate: "", toDate: "", isMain: false }]);
  const removeDesigHistoryRow = (index) => set("designationHistory", form.designationHistory.filter((_, i) => i !== index));

  async function handleSave() {
    setLoading(true);
    setError("");
    try {
      // Helper: get the key used in form for a field by its canonical name variants
      const getFieldKeyInternal = (fields, nameVariants) => {
        for (const variant of nameVariants) {
          const found = fields.find(f => (f.name || "").toLowerCase().trim() === variant.toLowerCase());
          if (found) return found.key || found.name.toLowerCase().replace(/[^a-z0-9]/g, "_");
        }
        return null;
      };

      // Resolve actual keys used for core fields from dynamic field definitions (profileFields)
      const nameKey = getFieldKeyInternal(profileFields, ["staff name", "name", "full name"]) || "name";
      const empKey = getFieldKeyInternal(profileFields, ["staff id", "emp id", "employee id", "empid"]) || "empId";
      const mobileKey = getFieldKeyInternal(profileFields, ["mobile", "contact number", "phone", "mobile number"]) || "mobile";
      const deptKey = getFieldKeyInternal(profileFields, ["department", "departments", "dept"]) || "department";
      const desigKey = getFieldKeyInternal(profileFields, ["designation", "designations"]) || "designation";

      const nameVal = form[nameKey] || form.name || "";
      const empVal = form[empKey] || form.empId || "";
      const mobileVal = form[mobileKey] || form.mobile || "";
      const deptVal = form[deptKey] || form.department || "";
      const desigVal = form[desigKey] || form.designation || "";

      // Sync aliases exactly like CreateFaculty.jsx
      const coreAliases = {};
      if (nameVal) coreAliases.name = nameVal;
      if (empVal) coreAliases.empId = empVal;
      if (mobileVal) coreAliases.mobile = mobileVal;
      if (deptVal) coreAliases.department = deptVal;
      if (desigVal) coreAliases.designation = desigVal;

      const updatedData = { 
        ...form,
        ...coreAliases
      };

      if (profileImage) {
        updatedData.profilePhotoURL = await compressImageToBase64(profileImage);
      }
      
      await api.put(`/faculty/${userId}/profile`, updatedData);
      onSuccess();
    } catch (err) { 
      console.error("Update Error:", err);
      alert("Error: " + err.message);
      setError("❌ Failed to update."); 
    }
    finally { setLoading(false); }
  }

  const inputStyle = { width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "10px 14px", fontSize: 16, color: "#1e293b", outline: "none", boxSizing: "border-box", fontFamily: "Georgia, serif" };
  const labelStyle = { fontSize: 14, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 24, padding: "36px", width: "100%", maxWidth: 500, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 32px 64px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
          <h2 style={{ fontWeight: 800, color: "#0f2557" }}>✏️ Edit Faculty Profile</h2>
          <span onClick={onClose} style={{ fontSize: 24, cursor: "pointer", color: "#94a3b8" }}>✕</span>
        </div>
        {/* Profile Photo Upload */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, background: "#f8fafc", borderRadius: 12, padding: "14px 16px", border: "1px solid #e2e8f0" }}>
          <div
            onClick={() => photoInputRef.current?.click()}
            style={{ width: 64, height: 64, borderRadius: "50%", border: "2px dashed #bfdbfe", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", background: "#eaf1ff", flexShrink: 0 }}
          >
            {profileImagePreview
              ? <img src={profileImagePreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontSize: 26, opacity: 0.5 }}>📷</span>
            }
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#374151", marginBottom: 6 }}>PROFILE PHOTO</div>
            <input
              ref={el => { photoInputRef.current = el; }}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={e => {
                const file = e.target.files[0];
                if (file) { setProfileImage(file); setProfileImagePreview(URL.createObjectURL(file)); }
              }}
            />
            <button type="button" onClick={() => photoInputRef.current?.click()}
              style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontWeight: 700, fontSize: 14, cursor: "pointer", marginRight: 8 }}>
              {profileImagePreview ? "🔄 Change" : "📤 Upload"}
            </button>
            {profileImage && <span style={{ fontSize: 13, color: "#16a34a", fontWeight: 600 }}>✅ {profileImage.name}</span>}
          </div>
        </div>
        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px", color: "#dc2626", fontSize: 15, marginBottom: 14 }}>{error}</div>}
        <div style={{ paddingBottom: 10 }}>
          {(() => {
            // Define core fields with grouping (Only keep Readonly Email as it's separate from dynamic fields)
            const coreFields = [
               { id: "email_readonly", type: "Readonly", name: "Email Address (Read-only)", val: form.email || "", group: "Contact Details" }
            ];

            // Combine with dynamic fields
            const allFormFields = [
              ...coreFields,
              ...profileFields.map(f => ({
                id: f.id,
                name: f.name,
                type: f.type,
                options: f.options,
                val: form[f.key || f.name.toLowerCase().replace(/[^a-z0-9]/g, "_")],
                group: f.group || "Other Details",
                required: f.required,
                key: f.key || f.name.toLowerCase().replace(/[^a-z0-9]/g, "_")
              }))
            ];

            // Group them
            const fieldGroups = allFormFields.reduce((acc, f) => {
              const g = f.group || "Other Details";
              if (!acc[g]) acc[g] = [];
              acc[g].push(f);
              return acc;
            }, {});

            // Sort groups
            const groupOrder = ["STAFF DETAILS", "PERSONAL DETAILS", "CONTACT DETAILS", "JOB DETAILS"];
            const sortedGroupKeys = Object.keys(fieldGroups).sort((a, b) => {
              const idxA = groupOrder.indexOf(a.toUpperCase());
              const idxB = groupOrder.indexOf(b.toUpperCase());
              if (idxA !== -1 && idxB !== -1) return idxA - idxB;
              if (idxA !== -1) return -1;
              if (idxB !== -1) return 1;
              return a.localeCompare(b);
            });

            return sortedGroupKeys.map(groupName => (
              <div key={groupName} style={{ marginBottom: 24, border: "1px solid #eef2f6", borderRadius: 16, overflow: "hidden" }}>
                <div style={{ padding: "10px 16px", background: "#f8fafc", borderBottom: "1px solid #eef2f6", fontSize: 13, fontWeight: 900, color: "#475569", letterSpacing: 1 }}>
                  {groupName.toUpperCase()}
                </div>
                <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  {fieldGroups[groupName].map(f => {
                    const k = f.id === "email_readonly" ? "email" : (f.key || f.id);
                    const fieldName = (f.name || "").toLowerCase().trim();
                    const label = f.name;
                    const fieldType = (f.type || "text").toLowerCase();
                    const isState = fieldName === "state" || fieldName === "state/ut";
                    const isDistrict = fieldName === "district";

                    return (
                      <div key={f.id} style={{ gridColumn: fieldType === "textarea" ? "1 / -1" : "auto" }}>
                        <label style={labelStyle}>{label} {f.required && <span style={{ color: "#dc2626" }}>*</span>}</label>
                        {f.type === "Readonly" ? (
                          <input value={f.val} disabled style={{ ...inputStyle, background: "#f1f5f9", cursor: "not-allowed" }} />
                        ) : isState ? (
                          <select value={form[k] || ""} onChange={e => {
                            set(k, e.target.value);
                            const dField = allFormFields.find(fd => (fd.name || "").toLowerCase().trim() === "district");
                            if (dField) set(dField.key || dField.id, "");
                          }} style={{ ...inputStyle, cursor: "pointer" }}>
                            <option value="">-- Select State --</option>
                            {Object.keys(INDIAN_STATES_DISTRICTS).map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : isDistrict ? (
                          <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }} disabled={!form[allFormFields.find(fd => (fd.name || "").toLowerCase().trim() === "state" || (fd.name || "").toLowerCase().trim() === "state/ut")?.key || "state"]}>
                            <option value="">-- Select District --</option>
                            {(() => {
                              const sField = allFormFields.find(fd => (fd.name || "").toLowerCase().trim() === "state" || (fd.name || "").toLowerCase().trim() === "state/ut");
                              const selectedState = form[sField?.key || sField?.id || "state"];
                              return (selectedState && INDIAN_STATES_DISTRICTS[selectedState]) ? INDIAN_STATES_DISTRICTS[selectedState].map(d => <option key={d} value={d}>{d}</option>) : null;
                            })()}
                          </select>
                        ) : fieldType === "dropdown" ? (
                          <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                            <option value="">-- Select --</option>
                            {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        ) : fieldType === "textarea" ? (
                          <textarea value={form[k] || ""} onChange={e => set(k, e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
                        ) : (
                          <input type={fieldType === "number" ? "number" : fieldType === "date" ? "date" : "text"}
                            value={form[k] || ""} onChange={e => set(k, e.target.value)}
                            style={inputStyle} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ));
          })()}
        </div>

        {/* Designation History in Modal */}
        <div style={{ marginTop: 24, borderTop: "2px solid #f1f5f9", paddingTop: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#0f2557" }}>Institutional Designation History</h3>
            <button onClick={addDesigHistoryRow} style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 8, padding: "4px 12px", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>+ Add Row</button>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
              <thead>
                <tr style={{ background: "#f8fafc", fontSize: 13, textAlign: "left" }}>
                  <th style={{ padding: "8px" }}>S.No</th>
                  <th style={{ padding: "8px" }}>Dept</th>
                  <th style={{ padding: "8px" }}>Desig</th>
                  <th style={{ padding: "8px" }}>Unit</th>
                  <th style={{ padding: "8px" }}>From</th>
                  <th style={{ padding: "8px" }}>To</th>
                  <th style={{ padding: "8px", textAlign: "center" }}>Main</th>
                  <th style={{ padding: "8px" }}></th>
                </tr>
              </thead>
              <tbody>
                {form.designationHistory.map((des, idx) => (
                  <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "6px 4px", fontSize: 14 }}>{idx + 1}</td>
                    <td style={{ padding: "6px 4px" }}>
                       <select value={des.dept} onChange={e => handleDesigHistoryChange(idx, "dept", e.target.value)} style={{ ...inputStyle, padding: "6px 8px", fontSize: 14 }}>
                        <option value="">--Sel--</option>
                        {departments.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "6px 4px" }}>
                       <select value={des.designation} onChange={e => handleDesigHistoryChange(idx, "designation", e.target.value)} style={{ ...inputStyle, padding: "6px 8px", fontSize: 14 }}>
                        <option value="">--Sel--</option>
                        {designations.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "6px 4px" }}>
                       <select value={des.unit} onChange={e => handleDesigHistoryChange(idx, "unit", e.target.value)} style={{ ...inputStyle, padding: "6px 8px", fontSize: 14 }}>
                        <option value="">--Sel--</option>
                        {["I", "II", "III", "IV", "V"].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "6px 4px" }}><input type="date" value={des.fromDate} onChange={e => handleDesigHistoryChange(idx, "fromDate", e.target.value)} style={{ ...inputStyle, padding: "4px 6px", fontSize: 13 }} /></td>
                    <td style={{ padding: "6px 4px" }}><input type="date" value={des.toDate} onChange={e => handleDesigHistoryChange(idx, "toDate", e.target.value)} style={{ ...inputStyle, padding: "4px 6px", fontSize: 13 }} /></td>
                    <td style={{ padding: "6px 4px", textAlign: "center" }}>
                      <input type="checkbox" checked={des.isMain} onChange={e => handleDesigHistoryChange(idx, "isMain", e.target.checked)} style={{ transform: "scale(1.1)" }} />
                    </td>
                    <td style={{ padding: "6px 4px" }}>
                      {form.designationHistory.length > 1 && (
                        <button onClick={() => removeDesigHistoryRow(idx)} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: 6, width: 24, height: 24, cursor: "pointer" }}>×</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <button onClick={onClose} style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 12, padding: "13px", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={loading} style={{ flex: 2, background: loading ? "#93c5fd" : "linear-gradient(135deg,#1d4ed8,#2563eb)", color: "#fff", border: "none", borderRadius: 12, padding: "13px", fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", fontFamily: "Georgia, serif" }}>
            {loading ? "⏳ Saving..." : "✅ Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DynamicItemCard({ item, index, sectionKey, sectionData, onEdit, onDelete }) {
  const [fields, setFields] = useState([]);

  useEffect(() => {
    async function loadFields() {
      try {
        const fieldsData = await api.get(`/section_fields_${sectionKey}?orderBy=order`);
        const data = Array.isArray(fieldsData.data) ? fieldsData.data : (fieldsData.data?.docs || []);
        setFields(data.length > 0 ? data : (SECTION_FIELDS[sectionKey] || []));
      } catch { setFields(SECTION_FIELDS[sectionKey] || []); }
    }
    loadFields();
  }, [sectionKey]);

  function getKey(f) { return f.key || f.name?.toLowerCase().replace(/[^a-z0-9]/g, "_"); }

  const titleField = fields.find(f => f.required);
  const title = titleField ? item[getKey(titleField)] : Object.values(item).find(v => v && typeof v === "string" && v !== "addedAt" && v !== "updatedAt");

  return (
    <div style={{ background: sectionData?.light, borderRadius: 12, padding: "14px 18px", borderLeft: `4px solid ${sectionData?.color}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ width: 26, height: 26, borderRadius: "50%", background: sectionData?.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{index}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 15, marginBottom: 5 }}>{title || "—"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "3px 14px" }}>
            {fields.filter(f => !f.required).map(f => {
              const k = getKey(f);
              const val = item[k];
              const type = (f.type || "").toLowerCase();
              if (!val) return null;
              if (type === "field" || (type === "file" && typeof val === "string" && val.startsWith("http"))) {
                return (
                  <div key={k} style={{ fontSize: 13, color: "#64748b" }}>
                    <a href={val} target="_blank" rel="noreferrer" style={{ color: "#2563eb", fontWeight: 700, textDecoration: "none" }}>📎 View File</a>
                  </div>
                );
              }
              return (
                <div key={k} style={{ fontSize: 13, color: "#64748b" }}>
                  <span style={{ fontWeight: 600, color: "#475569" }}>{f.label || f.name}:</span> {val}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
          {item.currentStatus && (
            <span style={{ background: item.currentStatus === "Published" || item.currentStatus === "Completed" || item.currentStatus === "Received" ? "#dcfce7" : "#fef3c7", color: item.currentStatus === "Published" || item.currentStatus === "Completed" || item.currentStatus === "Received" ? "#16a34a" : "#d97706", borderRadius: 20, padding: "3px 10px", fontSize: 13, fontWeight: 700 }}>
              {item.currentStatus}
            </span>
          )}
          <button onClick={onEdit} style={{ background: "#eaf1ff", color: "#2563eb", border: "none", borderRadius: 8, padding: "4px 10px", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>✏️</button>
          <button onClick={onDelete} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: 8, padding: "4px 10px", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>🗑</button>
        </div>
      </div>
    </div>
  );
}

function EditEntryModal({ section, sectionData, item, userId, onClose, onSuccess }) {
  const [form, setForm] = useState({ ...item });
  const [pendingFiles, setPendingFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [error, setError] = useState("");
  const [fields, setFields] = useState([]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    async function loadFields() {
      try {
        // Fetch from backend API with order sorting
        const fieldsData = await api.get(`/section_fields_${section}?orderBy=order`);
        if (Array.isArray(fieldsData)) {
          // Sort by order to ensure correct display
          const sorted = fieldsData.sort((a, b) => (a.order || 0) - (b.order || 0));
          setFields(sorted);
          console.log(`[EditEntryModal] Loaded ${sorted.length} fields for ${section}:`, sorted);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (e) {
        console.warn(`[EditEntryModal] Failed to load fields from API for ${section}:`, e);
        // Fallback to hardcoded fields if API fails
        setFields(SECTION_FIELDS[section] || []);
      }
      setFieldsLoading(false);
    }
    loadFields();
  }, [section]);

  function getKey(f) { return f.key || f.name?.toLowerCase().replace(/[^a-z0-9]/g, "_"); }

  async function handleSave() {
    const req = fields.find(f => f.required && !form[getKey(f)]?.toString().trim());
    if (req) return setError(`${req.label || req.name} is required`);
    try {
      setLoading(true);
      let finalForm = { ...form, updatedAt: new Date().toISOString() };
      onSuccess(item, finalForm);
    } catch (err) { console.error(err); setError("Failed to update."); }
    finally { setLoading(false); }
  }

  const inputStyle = { width: "100%", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10, padding: "9px 12px", fontSize: 15, color: "#1e293b", outline: "none", boxSizing: "border-box", fontFamily: "Georgia, serif" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 24, padding: "32px", width: "100%", maxWidth: 620, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 32px 64px rgba(0,0,0,0.2)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 22 }}>
          <h2 style={{ fontWeight: 800, color: "#0f2557", fontSize: 22 }}>{sectionData?.icon} Edit {sectionData?.label}</h2>
          <span onClick={onClose} style={{ fontSize: 24, cursor: "pointer", color: "#94a3b8" }}>✕</span>
        </div>
        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px", color: "#dc2626", fontSize: 15, marginBottom: 14 }}>{error}</div>}
        {fieldsLoading ? <div style={{ textAlign: "center", padding: "30px 0", color: "#94a3b8" }}>⏳ Loading...</div> : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {fields.map((f, i) => {
              const k = getKey(f);
              const type = (f.type || "text").toLowerCase();
              return (
                <div key={i} style={{ gridColumn: type === "textarea" ? "1/-1" : "auto" }}>
                  <label style={{ fontSize: 14, fontWeight: 700, color: "#374151", display: "block", marginBottom: 5 }}>
                    {f.label || f.name} {f.required && <span style={{ color: "#dc2626" }}>*</span>}
                  </label>
                  {type === "dropdown" ? (
                    <select value={form[k] || ""} onChange={e => set(k, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                      <option value="">-- Select --</option>
                      {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : type === "textarea" ? (
                    <textarea value={form[k] || ""} onChange={e => set(k, e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
                  ) : type === "file" ? (
                    <div>
                      {form[k] && typeof form[k] === "string" && form[k].startsWith("http") && (
                        <div style={{ fontSize: 13, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ color: "#16a34a", fontWeight: 700 }}>✅ Current file: </span>
                          <a href={form[k]} target="_blank" rel="noreferrer" style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}>View</a>
                          <span style={{ color: "#94a3b8" }}>(Select new to replace)</span>
                        </div>
                      )}
                      <input type="file" onChange={e => setPendingFiles(prev => ({ ...prev, [k]: e.target.files[0] }))} style={{ ...inputStyle, padding: "6px 12px" }} />
                      {pendingFiles[k] && <div style={{ fontSize: 13, color: "#16a34a", marginTop: 4 }}>📎 New file selected: {pendingFiles[k].name}</div>}
                    </div>
                  ) : type === "number" ? (
                    <input type="number" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  ) : type === "date" ? (
                    <input type="date" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  ) : (type === "month-year" || type === "Month-Year") ? (
                    <input type="month" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  ) : (
                    <input type="text" value={form[k] || ""} onChange={e => set(k, e.target.value)} style={inputStyle} />
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: "flex", gap: 12, marginTop: 22 }}>
          <button onClick={onClose} style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "none", borderRadius: 12, padding: "12px", fontWeight: 700, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={loading} style={{ flex: 2, background: loading ? "#93c5fd" : `linear-gradient(135deg,${sectionData?.color},${sectionData?.color}cc)`, color: "#fff", border: "none", borderRadius: 12, padding: "12px", fontWeight: 800, cursor: loading ? "not-allowed" : "pointer", fontFamily: "Georgia, serif" }}>
            {loading ? "⏳ Saving..." : "✅ Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
